import { FC, useMemo, SetStateAction } from 'react';
import { Stack, MenuItem, TextField, Typography, IconButton } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { DateRange, DateOperator, FilterCondition } from '@/types/entities/marketing-list';
import Iconify from '@/components/media/iconify';

type Props = {
  name: string;
  label: string;
  filters: Record<string, any>;
  onChange: (value: SetStateAction<any>) => void;
  required?: boolean;
  allowedOperators?: DateOperator[];
};

/**
 * Advanced date filter component
 * Supports date operators like before, after, between, and presets (last 7 days, last month, etc.)
 */
export const AdvancedDateFilter: FC<Props> = ({
  name,
  label,
  filters,
  onChange,
  required = false,
  allowedOperators = [
    DateOperator.EQUALS,
    DateOperator.BEFORE,
    DateOperator.AFTER,
    DateOperator.BETWEEN,
    DateOperator.LAST_7_DAYS,
    DateOperator.LAST_30_DAYS,
    DateOperator.LAST_90_DAYS,
    DateOperator.THIS_MONTH,
    DateOperator.LAST_MONTH,
    DateOperator.THIS_YEAR,
    DateOperator.LAST_YEAR,
  ],
}) => {
  const { t, i18n } = useTranslation();

  const condition: FilterCondition<Date | DateRange> | undefined = useMemo(
    () => filters?.advancedFilters?.[name],
    [filters, name]
  );

  const currentOperator = condition?.operator || DateOperator.LAST_30_DAYS;

  // Operators that don't require manual date selection
  const presetOperators = [
    DateOperator.LAST_7_DAYS,
    DateOperator.LAST_30_DAYS,
    DateOperator.LAST_90_DAYS,
    DateOperator.THIS_MONTH,
    DateOperator.LAST_MONTH,
    DateOperator.THIS_YEAR,
    DateOperator.LAST_YEAR,
  ];

  const requiresDate =
    !presetOperators.includes(currentOperator as DateOperator) &&
    currentOperator !== DateOperator.BETWEEN;
  const requiresRange = currentOperator === DateOperator.BETWEEN;

  const handleOperatorChange = (operator: string) => {
    let value: Date | DateRange | undefined;

    // For preset operators, no value needed
    if (!presetOperators.includes(operator as DateOperator)) {
      if (operator === DateOperator.BETWEEN) {
        value = { from: undefined, to: undefined };
      } else {
        value = new Date();
      }
    }

    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      advancedFilters: {
        ...(prevState.advancedFilters || {}),
        [name]: {
          operator,
          value,
        },
      },
    }));
  };

  const handleDateChange = (date: moment.Moment | null) => {
    if (!date) return;

    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      advancedFilters: {
        ...(prevState.advancedFilters || {}),
        [name]: {
          operator: currentOperator,
          value: date.toDate(),
        },
      },
    }));
  };

  const handleRangeChange = (date: moment.Moment | null, field: 'from' | 'to') => {
    if (!date) return;

    const currentRange = (condition?.value as DateRange) || { from: undefined, to: undefined };

    onChange((prevState: Record<string, any>) => ({
      ...prevState,
      advancedFilters: {
        ...(prevState.advancedFilters || {}),
        [name]: {
          operator: currentOperator,
          value: {
            ...currentRange,
            [field]: date.toDate(),
          },
        },
      },
    }));
  };

  const handleClear = () => {
    onChange((prevState: Record<string, any>) => {
      const { [name]: removed, ...rest } = prevState.advancedFilters || {};
      return {
        ...prevState,
        advancedFilters: rest,
      };
    });
  };

  const getOperatorLabel = (operator: DateOperator): string => {
    const labels: Record<DateOperator, string> = {
      [DateOperator.EQUALS]: t('Equals'),
      [DateOperator.BEFORE]: t('Before'),
      [DateOperator.AFTER]: t('After'),
      [DateOperator.BETWEEN]: t('Between'),
      [DateOperator.LAST_7_DAYS]: t('Last 7 days'),
      [DateOperator.LAST_30_DAYS]: t('Last 30 days'),
      [DateOperator.LAST_90_DAYS]: t('Last 90 days'),
      [DateOperator.THIS_MONTH]: t('This month'),
      [DateOperator.LAST_MONTH]: t('Last month'),
      [DateOperator.THIS_YEAR]: t('This year'),
      [DateOperator.LAST_YEAR]: t('Last year'),
    };
    return labels[operator] || operator;
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{label}</Typography>
        {!required && condition && (
          <IconButton size="small" onClick={handleClear} sx={{ ml: 1 }}>
            <Iconify icon="solar:close-circle-bold" width={18} />
          </IconButton>
        )}
      </Stack>

      <TextField
        select
        size="small"
        value={currentOperator}
        onChange={(e) => handleOperatorChange(e.target.value)}
        fullWidth
      >
        {allowedOperators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {getOperatorLabel(operator)}
          </MenuItem>
        ))}
      </TextField>

      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
        {requiresDate && (
          <DatePicker
            label={t('Date')}
            value={condition?.value ? moment(condition.value as Date) : null}
            onChange={handleDateChange}
          />
        )}

        {requiresRange && (
          <Stack spacing={1.5}>
            <DatePicker
              label={t('From')}
              value={
                condition && (condition.value as DateRange)?.from
                  ? moment((condition.value as DateRange).from)
                  : null
              }
              onChange={(date) => handleRangeChange(date, 'from')}
            />
            <DatePicker
              label={t('To')}
              value={
                condition && (condition.value as DateRange)?.to
                  ? moment((condition.value as DateRange).to)
                  : null
              }
              onChange={(date) => handleRangeChange(date, 'to')}
            />
          </Stack>
        )}
      </LocalizationProvider>
    </Stack>
  );
};
